import { auth, db, functions } from '@/plugins/firebase'
import { lessonsForTeacher } from '@/utils/lessonHours'

export default {

  // Contrasenas de profesores. No se leen del documento del profesor: viven cifradas en otra
  // coleccion y solo las devuelve una funcion que comprueba que quien llama es admin.
  async getTeacherPassword (teacherId) {
    const callable = functions.httpsCallable('tslcGetTeacherPassword')
    const response = await callable({ teacherId })
    return response?.data
  },

  // El correo se cambia en el servidor porque hay que tocar Firebase Auth (es contra lo que se
  // hace login) y las copias del profesor que guarda cada clase. Escribirlo solo en el
  // documento de 'teachers', como se hacia antes, dejaba el login y el Calendar con el viejo.
  async updateTeacherEmail (teacherId, email) {
    const callable = functions.httpsCallable('tslcUpdateTeacherEmail')
    const response = await callable({ teacherId, email })
    if (!response?.data?.success) {
      throw new Error('El servidor no confirmo el cambio de correo')
    }
    return response.data
  },


  // Eliminar un profesor tambien tiene que borrar su cuenta de acceso, no solo su documento.
  async deleteTeacherAccount (teacherId, force) {
    const callable = functions.httpsCallable('tslcDeleteTeacher')
    const response = await callable({ teacherId, force: !!force })
    if (!response?.data?.success) {
      throw new Error('El servidor no confirmo la eliminacion')
    }
    return response.data
  },

  async setTeacherPassword (teacherId, password) {
    const callable = functions.httpsCallable('tslcSetTeacherPassword')
    const response = await callable({ teacherId, password })
    if (!response?.data?.success) {
      throw new Error('El servidor no confirmo el cambio de contrasena')
    }
    return response.data
  },
  async fetchClasses () {
    const classesRef = await db.collection('classes').get()
    const classes = []
    classesRef.forEach(doc => {
      const classObject = doc.data()
      classObject.id = doc.id
      classes.push(classObject)
    })
    return classes
  },

  async addClass (classObject) {
    const docRef = await db.collection('classes').add(classObject)
    const createdClass = (await docRef.get()).data()
    createdClass.id = docRef.id
    return createdClass
  },

  async getClassById (id) {
    const classRef = await db.collection('classes').doc(id).get()
    const classObject = classRef.data()
    classObject.id = classRef.id
    return classObject
  },
  async editClass (classId, classObject) {
    // eslint-disable-next-line no-console
    console.log('S1. scheduleService.editClass iniciado con classId:', classId)
    // eslint-disable-next-line no-console
    console.log('S2. classObject recibido:', JSON.stringify(classObject))
    try {
      // eslint-disable-next-line no-console
      console.log('S3. Obteniendo referencia a la clase')
      const editedClassRef = db.collection('classes').doc(classId)

      // eslint-disable-next-line no-console
      console.log('S4. Actualizando documento en Firestore')
      await editedClassRef.update(classObject)

      // eslint-disable-next-line no-console
      console.log('S5. Obteniendo documento actualizado')
      const doc = await editedClassRef.get()

      if (!doc.exists) {
        // eslint-disable-next-line no-console
        console.error('S6. Error: Documento no existe después de actualizar')
        throw new Error('La clase no se encontró después de actualizar')
      }

      // eslint-disable-next-line no-console
      console.log('S7. Documento existe, extrayendo datos')
      const editedClassObject = doc.data()
      editedClassObject.id = doc.id

      // eslint-disable-next-line no-console
      console.log('S8. Retornando objeto actualizado con id:', editedClassObject.id)
      return editedClassObject
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('S9. Error en scheduleService.editClass:', error)
      throw error // Re-lanzar el error para que lo maneje el llamador
    }
  },
  async getClassByTeacher (teacher) {
    const classRef = await db.collection('classes').where('teacher.id', '==', teacher.id).get()
    const classArray = []
    classRef.forEach(doc => {
      const classObject = doc.data()
      classObject.id = doc.id
      classArray.push(classObject)
    })
    return classArray
  },

  async getAllTeachers () {
    const teachersRef = await db.collection('teachers').get()
    const teachers = []
    teachersRef.forEach(doc => {
      const teacherObject = doc.data()
      teacherObject.id = doc.id
      teachers.push(teacherObject)
    })
    return teachers
  },

  async getAllLevels () {
    const levelsRef = await db.collection('levels').orderBy('order').get()
    const levels = []
    levelsRef.forEach(doc => {
      const levelObject = doc.data()
      levelObject.id = doc.id
      levels.push(levelObject)
    })
    return levels
  },

  async createLesson (lesson) {
    const lessonRef = await db.collection('lessons').add(lesson)
    const lessonData = (await lessonRef.get()).data()
    lessonData.id = lessonRef.id
    return lesson
  },

  // Registra la leccion dictada. Es la fuente del reporte mensual de horas del profesor.
  //
  // La escritura se perdio en una actualizacion de la plataforma: createLesson quedo sin
  // llamadores y onCreateLesson (la callable) responde { success: true } sin escribir nada,
  // asi que desde entonces ninguna clase queda registrada y las horas no acumulan.
  //
  // La forma del documento replica la de los registros historicos de 2022-2025:
  //   { class, createdAt, startDate, state, students, week }
  //
  // Idempotente por (class.id + startDate): reguardar la asistencia de la misma sesion
  // actualiza el documento en vez de crear otro. Sin esto, dar dos veces a "Guardar cambios"
  // duplicaria las horas del profesor.
  async createOrUpdateLesson (lesson) {
    const existing = await db.collection('lessons')
      .where('class.id', '==', lesson.class.id)
      .where('startDate', '==', lesson.startDate)
      .limit(1).get()

    if (!existing.empty) {
      const docRef = existing.docs[0].ref
      await docRef.update({ ...lesson, updatedAt: new Date() })
      return { id: docRef.id, created: false }
    }

    const lessonRef = await db.collection('lessons').add({ ...lesson, createdAt: new Date() })
    return { id: lessonRef.id, created: true }
  },

  // Lecciones que entran en el reporte de horas de un profesor: las de sus clases mas las
  // que cubrio a otro. Quien cubre, cobra; el titular que no la dio, no.
  //
  // Son dos consultas porque es un OR sobre dos campos distintos, y el SDK compat de
  // firebase 9 no expone Filter.or. La union y el descarte de las propias que cubrio otro
  // estan en lessonsForTeacher, que es pura y tiene test.
  //
  // Sin orderBy a proposito: Firestore excluye del resultado los documentos que no tienen
  // el campo por el que se ordena, sin lanzar error. Si una leccion se escribe sin
  // 'createdAt' (el repo mezcla createdAt y created_at), existe pero el reporte no la ve.
  // El orden lo aplica igualmente el modal por startDate.
  async getLessonsByTeacher (teacherId) {
    const leer = async (query) => {
      const snapshot = await query.get()
      const lessons = []
      snapshot.forEach(doc => {
        const lesson = doc.data()
        lesson.id = doc.id
        lessons.push(lesson)
      })
      return lessons
    }

    const [propias, cubiertas] = await Promise.all([
      leer(db.collection('lessons').where('class.teacher.id', '==', teacherId)),
      // Las lecciones sin 'coveredBy' no entran aqui: Firestore las excluye sola.
      leer(db.collection('lessons').where('coveredBy.id', '==', teacherId))
    ])

    return lessonsForTeacher(propias, cubiertas, teacherId)
  },

  async getMyTeacherUser () {
    const currentUser = await auth.currentUser
    if (currentUser) {
      const teacherRef = await db.collection('teachers').where('uid', '==', currentUser.uid).limit(1).get()
      if (!teacherRef.empty) {
        const teacher = teacherRef.docs[0].data()
        teacher.id = teacherRef.docs[0].id
        return teacher
      }
      return null
    } else {
      return null
    }
  },

  async getNextLevel (levelObject) {
    const currentLevelRef = await db.collection('levels').doc(levelObject.id).get()
    const levelRef = await db.collection('levels').orderBy('order').startAfter(currentLevelRef).get()
    let level = null
    if (!levelRef.empty) {
      level = levelRef.docs[0].data()
      level.id = levelRef.docs[0].id
    }
    return level
  },

  async deleteLesson (lesson) {
    await db.collection('lessons').doc(lesson.id).delete()
  },

  async getAllMaterials () {
    const materialsRef = await db.collection('materials').orderBy('order').get()
    const materials = []
    materialsRef.forEach(doc => {
      const materialObject = doc.data()
      materialObject.id = doc.id
      materials.push(materialObject)
    })
    return materials
  },

  // Schedule tab
  async createStudent (student) {
    const studentRef = await db.collection('students').add({
      ...student,
      flashcardsEnabled: student.flashcardsEnabled !== undefined ? student.flashcardsEnabled : true,
      createdAt: new Date(),
      assistance: [],
      assistanceCounter: 0
    })
    const studentObject = (await studentRef.get()).data()
    studentObject.id = studentRef.id
    return studentObject
  },
  async updateStudent (studentId, data) {
    const studentRef = await db.collection('students').doc(studentId)
    await studentRef.update(data)
    const studentObject = (await studentRef.get()).data()
    studentObject.id = studentRef.id
    return studentObject
  },
  async updateOrCreateStudent (student) {
    const studentsSnapshot = await db.collection('students').where('email', '==', student.email).limit(1).get()
    let created = false
    if (studentsSnapshot.empty) {
      created = true
      return { student: await this.createStudent(student), created }
    }
    return { student: await this.updateStudent(studentsSnapshot.docs[0].id, student), created }
  },

  async sendMailWhenCreatingGuestUser (student, classObject) {
    try {
      console.log('Enviando correo al crear usuario invitado con datos:', {
        student: {
          ...student,
          // Ocultando datos sensibles si existen
          password: student.password ? '********' : undefined
        },
        class: classObject
      })

      // tslcSendGuestInvitationMails reemplaza a sendMailWhenCreatingGuestUser, cuyo SMTP
      // dejo de autenticar. Manda los dos correos: al invitado y al profesor de la clase.
      const callable = functions.httpsCallable('tslcSendGuestInvitationMails')
      console.log('Llamando función callable: tslcSendGuestInvitationMails')

      const response = await callable({ student, class: classObject })
      console.log('Respuesta de sendMailWhenCreatingGuestUser (callable):', response?.data)

      if (!response?.data || !response.data.success) {
        throw new Error(response?.data?.error || 'Respuesta inválida del servidor')
      }

      return response.data
    } catch (error) {
      console.error('Error en sendMailWhenCreatingGuestUser (callable):', {
        message: error?.message,
        code: error?.code,
        details: error?.details,
        stack: error?.stack
      })
      throw error
    }
  },

  async getStudent (studentId) {
    const studentSnapshot = await db.collection('students').doc(studentId).get()
    const student = studentSnapshot.data()
    student.id = studentSnapshot.id
    return student
  },

  async editStudent (studentId, student) {
    const studentRef = await db.collection('students').doc(studentId)
    await studentRef.update(student)
    const editedStudent = (await studentRef.get()).data()
    editedStudent.id = studentRef.id
    return editedStudent
  },

  async removeClassFromStudent (studentId, classId) {
    await db.collection(`students/${studentId}/myCourses`).doc(classId).delete()
    const studentObject = await this.getStudent(studentId)
    let assignedCoursesIds = studentObject.assignedCoursesIds || []
    assignedCoursesIds = assignedCoursesIds.filter((item) => item !== classId)
    await this.editStudent(studentId, { assignedCoursesIds })
  }
}
